import {
  IInventoryPurchaseOrderDraftProductInfo,
  IInventoryPurchaseOrderProductSearchResponseData,
  IPOInvoiceOcrItem,
  IPOTaxCalculationFields,
} from "@/api_framework/api_modals/Inventory";
import BulkUploadModal from "@/app/components/routes/modals/BulkUpload";
import AlertError from "@/app/components/ui/Alerts/Error";
import Input from "@/app/components/ui/Inputs/Input";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import SearchableSelectMenu from "./PoSelectMenu";
import { useInventoryPurchaseOrderBulkClearAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryPurchaseOrderBulkClearAPI";
import { useInventoryPurchaseOrderInformationCalculationAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryPurchaseOrderInformationCalculationAPI";
import { usePODiscountAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePODiscountAPI";
import { usePOUnitAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePOUnitAPI";
import { usePurchaseOrderDetailsAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePurchaseOrderDetailsAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { TrashIcon } from "@heroicons/react/24/outline";
import { isObject, toNumber } from "lodash";
import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import toast from "react-hot-toast";
import { FiUpload } from "react-icons/fi";
import { RiInformationLine } from "react-icons/ri";
import { IInventoryPurchaseOrderFormFields } from "..";
import InventoryProductSearchComponent from "../InventoryProductSearchComponent";
import { useAwaitingGRNDeletePOAPI } from "@/app/hooks/api_hooks/Inventory/PO/useAwaitingGRNDeletePOAPI";
import { usePurchaseOrderViewAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePurchaseOrderViewAPI";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { CheckIcon } from "@heroicons/react/24/outline";
import OcrBulkDecisionModal, { OcrBulkDecision } from "./OcrBulkDecisionModal";

const FETCH_TAX_BUTTON_WIDTH = 136;
const FETCH_TAX_BUTTON_HEIGHT = 32;

/**
 * The invoice-verification decision, exposed to the submit button so "Open For GRN" can stop and ask
 * before a PO with undecided invoice differences moves on. Null whenever the PO is not in that step.
 */
export interface IOcrVerificationGate {
  hasPendingChanges: boolean;
  changeSummary: (decision: OcrBulkDecision) => string[];
  applyBulkDecision: (decision: OcrBulkDecision) => Promise<void>;
  /** The decisions to persist with a verification save — see constructPOUpdatePayload. */
  getSaveMarkers: () => {
    acceptedVariantIds: string[];
    rejectedOcrItemIndexes: number[];
    rejectedLines: IInventoryPurchaseOrderDraftProductInfo[];
  };
}

interface IProductInfoFormFieldsProps {
  formHook: UseFormReturn<IInventoryPurchaseOrderFormFields, any>;
  POProductsHook: UseFieldArrayReturn<IInventoryPurchaseOrderFormFields, "product_info", "id">;
  ocrGateRef?: MutableRefObject<IOcrVerificationGate | null>;
  /** Holds the recalculation in flight, so the parent's save buttons can wait for fresh totals. */
  productCalculationRef?: MutableRefObject<Promise<unknown> | null>;
  /** Saves the PO in place; called each time the user changes the invoice verification. */
  onInvoiceVerificationChange?: () => Promise<unknown> | void;
}

/**
 * Fields the scanned invoice is allowed to overwrite on a PO line. Tax rates are deliberately absent:
 * frozen_tax_details is owned by the backend tax engine and is refreshed by Tax Refresh. HSN is absent
 * too: it comes from the product catalogue and the scan must never change it.
 */
const OCR_COMPARED_FIELDS = [
  { poField: "package_count", ocrField: "quantity", label: "Qty" },
  { poField: "price_per_package", ocrField: "unit_price", label: "Cost/pkg" },
  { poField: "discount_percentage", ocrField: "discount_percentage", label: "Discount" },
] as const;

const OCR_NUMERIC_TOLERANCE = 0.01;

const getOcrDiffs = (poItem: IInventoryPurchaseOrderDraftProductInfo, ocrItem: IPOInvoiceOcrItem) =>
  OCR_COMPARED_FIELDS.filter(({ poField, ocrField }) => {
    const ocrValue = ocrItem?.[ocrField];
    if (ocrValue === null || ocrValue === undefined) return false;
    const po = Number(poItem?.[poField] ?? 0);
    const ocr = Number(ocrValue);
    return !Number.isNaN(po) && !Number.isNaN(ocr) && Math.abs(po - ocr) > OCR_NUMERIC_TOLERANCE;
  }).map(({ poField, ocrField, label }) => ({
    field: poField as string,
    label,
    poValue: poItem?.[poField] ?? "—",
    ocrValue: ocrItem?.[ocrField],
  }));

/**
 * The invoice reports the four GST components; a PO line carries a single rate. Inter-state invoices
 * fill igst, intra-state ones split across cgst/sgst, union territories use utgst.
 */
const getOcrTaxRate = (ocrItem: IPOInvoiceOcrItem) => {
  const igst = Number(ocrItem?.igst_percentage ?? 0);
  if (igst > 0) return igst;
  const splitRate = Number(ocrItem?.cgst_percentage ?? 0) + Number(ocrItem?.sgst_percentage ?? 0);
  if (splitRate > 0) return splitRate;
  return Number(ocrItem?.utgst_percentage ?? 0);
};

const roundToPaise = (value: number) => Math.round(value * 100) / 100;

/** Folds one more invoice batch of the same SKU in: quantities add up, price and discount are weighted. */
const mergeOcrBatch = (seen: IPOInvoiceOcrItem, item: IPOInvoiceOcrItem): IPOInvoiceOcrItem => {
  const grossOf = (line: IPOInvoiceOcrItem) => Number(line?.quantity ?? 0) * Number(line?.unit_price ?? 0);
  const netOf = (line: IPOInvoiceOcrItem) => grossOf(line) * (1 - Number(line?.discount_percentage ?? 0) / 100);
  const quantity = Number(seen.quantity ?? 0) + Number(item.quantity ?? 0);
  const gross = grossOf(seen) + grossOf(item);
  const net = netOf(seen) + netOf(item);
  return {
    ...seen,
    quantity,
    unit_price: quantity ? roundToPaise(gross / quantity) : seen.unit_price,
    discount_percentage: gross ? roundToPaise((1 - net / gross) * 100) : seen.discount_percentage,
  };
};


const withDerivedTotals = (
  line: IInventoryPurchaseOrderDraftProductInfo,
): IInventoryPurchaseOrderDraftProductInfo => {
  const count = Number(line?.package_count ?? 0);
  const price = Number(line?.price_per_package ?? 0);
  const discount = Number(line?.discount_percentage ?? 0);
  if (!Number.isFinite(count) || !Number.isFinite(price) || !Number.isFinite(discount)) return line;

  const beforeTax = roundToPaise(count * price);
  const discountAmount = roundToPaise(
    line?.discount_type === "PERCENTAGE" ? (beforeTax * discount) / 100 : discount,
  );
  const afterDiscount = roundToPaise(beforeTax - discountAmount);
  const taxRate = parseFloat(String(line?.tax_details ?? "").replace("%", ""));

  return {
    ...line,
    total_cost_before_tax: beforeTax,
    total_discount_before_tax: discountAmount,
    total_cost_after_discount: afterDiscount,
    total_cost_after_tax: Number.isNaN(taxRate)
      ? line?.total_cost_after_tax
      : roundToPaise(afterDiscount * (1 + taxRate / 100)),
  };
};

type OcrLineStatus = "matched" | "accepted" | "missing" | "extra" | "rejected";

// One step darker than the row tint they sit on, so the pill stays legible.
const OCR_STATUS_STYLES: Record<OcrLineStatus, { label: string; className: string }> = {
  matched: { label: "Matched", className: "bg-green-100 text-green-700 border-green-300" },
  accepted: { label: "Accepted", className: "bg-green-100 text-green-700 border-green-300" },
  missing: { label: "Not in invoice", className: "bg-amber-100 text-amber-700 border-amber-300" },
  extra: { label: "Extra in invoice", className: "bg-blue-100 text-blue-700 border-blue-300" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-700 border-red-300" },
};

/**
 * Row tints for the invoice-verification pass. Sticky cells need the darker step so they stay
 * opaque and keep the same contrast against the row that `bg-gray-100` gives on a normal line.
 */
const OCR_ROW_TINTS: Record<OcrLineStatus, { row: string; stickyCell: string }> = {
  matched: { row: "bg-green-50", stickyCell: "bg-green-100" },
  accepted: { row: "bg-green-50", stickyCell: "bg-green-100" },
  missing: { row: "bg-amber-50", stickyCell: "bg-amber-100" },
  extra: { row: "bg-blue-50", stickyCell: "bg-blue-100" },
  rejected: { row: "bg-red-50", stickyCell: "bg-red-100" },
};

const OcrStatusPill: React.FC<{ status: OcrLineStatus }> = ({ status }) => (
  <span
    className={`inline-flex items-center mt-1 px-2 py-0.5 border rounded-full font-medium text-[10px] ${OCR_STATUS_STYLES[status].className}`}
  >
    {OCR_STATUS_STYLES[status].label}
  </span>
);

/** Four-point spark, the visual shorthand for "this value came from the AI invoice scan". */
const OcrAiSparkIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
    <defs>
      <linearGradient id="ocr-ai-spark" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#4285F4" />
        <stop offset="55%" stopColor="#9B72F2" />
        <stop offset="100%" stopColor="#D96570" />
      </linearGradient>
    </defs>
    <path
      fill="url(#ocr-ai-spark)"
      d="M12 1.5c.36 3.02 1.3 5.24 2.82 6.68C16.34 9.62 18.6 10.5 21.6 12c-3 1.5-5.26 2.38-6.78 3.82C13.3 17.26 12.36 19.48 12 22.5c-.36-3.02-1.3-5.24-2.82-6.68C7.66 14.38 5.4 13.5 2.4 12c3-1.5 5.26-2.38 6.78-3.82C10.7 6.74 11.64 4.52 12 1.5Z"
    />
  </svg>
);

/**
 * Marks a cell the invoice scan rewrote, alongside the PO's own value so the change is auditable.
 * Renders inline: a changed cell must stay one line tall or it knocks the row out of alignment.
 */
const OcrAiChangedValue: React.FC<{ previousValue: React.ReactNode }> = ({ previousValue }) => (
  <span
    title="Updated from the scanned invoice"
    className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 whitespace-nowrap shrink-0"
  >
    <OcrAiSparkIcon className="w-3 h-3 shrink-0" />
    <span className="line-through">{previousValue}</span>
  </span>
);

const OCR_ICON_BUTTON_STYLES = {
  accept: "bg-white border-green-300 text-green-600 hover:bg-green-100",
  reject: "bg-white border-red-300 text-red-600 hover:bg-red-100",
  neutral: "bg-white border-gray-300 text-gray-500 hover:bg-gray-100",
};

const OcrIconButton: React.FC<{
  title: string;
  onClick: () => void;
  disabled?: boolean;
  variant: keyof typeof OCR_ICON_BUTTON_STYLES;
  children: React.ReactNode;
}> = ({ title, onClick, disabled, variant, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
    className={`inline-flex justify-center items-center border rounded-md w-7 h-7 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${OCR_ICON_BUTTON_STYLES[variant]}`}
  >
    {children}
  </button>
);

const ProductInfoFormFields: React.FC<IProductInfoFormFieldsProps> = ({
  POProductsHook,
  formHook,
  ocrGateRef,
  productCalculationRef,
  onInvoiceVerificationChange,
}) => {
  const { forOnlyNumberWithoutZero, forNumericWithDecimal, forAllAcceptWithOutStartWithDotAndSpace } =
    useFormValidations();
  const { setVisible: bulkUpload } = useModalActions("BULK_UPLOAD_MODAL");
  const [showTooltip, setShowTooltip] = useState(false);
  const { getRouteKey } = useRouter();
  const [editPermission, setEditPermission] = useState<boolean>(false);
  const { execute: fetchPODetails } = usePurchaseOrderDetailsAPI();
  const { execute: calculateProductInfo } = useInventoryPurchaseOrderInformationCalculationAPI();
  const { execute: deletePOAwaitingGRN } = useAwaitingGRNDeletePOAPI();
  const [newlyAddedVariants, setNewlyAddedVariants] = useState<string[]>([]);
  // Invoice-verification decisions are final — there is no undo once a line is accepted or rejected.
  const [ocrDecisions, setOcrDecisions] = useState<Record<string, "accepted" | "rejected">>({});
  // PO lines the user rejected: pulled out of the form data, kept here so the row stays on screen in red.
  const [ocrRejectedLines, setOcrRejectedLines] = useState<IInventoryPurchaseOrderDraftProductInfo[]>([]);
  // Invoice rows (by index) accepted into the PO as extras. One SKU can come as several batch rows,
  // each accepted on its own; its PO line carries the accepted batches added together.
  const [acceptedExtraRows, setAcceptedExtraRows] = useState<number[]>([]);
  // Which bulk confirmation popup is open, if any.
  const [ocrBulkDecision, setOcrBulkDecision] = useState<OcrBulkDecision | null>(null);
  // variant_id → { field: the PO's value before the invoice overwrote it }
  const [ocrOverwrittenFields, setOcrOverwrittenFields] = useState<
    Record<string, Partial<Record<string, string | number>>>
  >({});
  const ocrAppliedForPOIdRef = useRef<string | null>(null);
  // Lines on screen when Tax Refresh was pressed during invoice verification. The refresh reloads the
  // saved PO, which would otherwise wipe the scan's values and the accept/reject decisions.
  const linesBeforeTaxRefreshRef = useRef<{ poId: string; lines: IInventoryPurchaseOrderDraftProductInfo[] } | null>(
    null,
  );
  const userPermissions = useAppSelector((state) => state?.auth?.userPermissions);
  const {
    purchaseOrderDetails: PODetails,
    unitList,
    discountList,
  } = useRouteInfo(getRouteKey("INVENTORY", "id"))?.routeState?.state as IInventoryState;
  const fetchUnitListAPI = usePOUnitAPI();
  const fetchDiscountListAPI = usePODiscountAPI();
  const bulkClearApi = useInventoryPurchaseOrderBulkClearAPI();
  const { execute: fetchUpdatedTaxDetails } = usePurchaseOrderViewAPI()
  const [isUpdatingTax, setIsUpdatingTax] = useState(false);
  const [isProductTableLoading, setIsProductTableLoading] = useState(false);
  const unitOptions = useSelectMenuReducer(unitList, "name", "id");
  const discountOptions = useSelectMenuReducer(discountList, "discount_symbol", "discount_type");

  // RBAC: PO_CREATE_ACTION — legacy roles: clinic_ops_bluecity_PO, central_ops, Category_Ops
  const isCentralOps = useFeatureAccess("PO_CREATE_ACTION");
  const isCreationEditableStatus = PODetails?.po_status === "pre_draft" || PODetails?.po_status === "draft";
  const isEditableProductStatus =
    isCreationEditableStatus ||
    PODetails?.po_status === "pending_invoice_verification" ||
    PODetails?.po_status === "pending_invoice_upload" ||
    PODetails?.po_status === "issue_in_invoice_upload" ||
    PODetails?.po_status === "awaiting_grn";
  const isLineItemEditableStatus =
    isCreationEditableStatus ||
    PODetails?.po_status === "pending_invoice_upload" ||
    PODetails?.po_status === "pending_invoice_verification" ||
    PODetails?.po_status === "issue_in_invoice_upload" ||
    PODetails?.po_status === "awaiting_grn";
  const isEditModePriceDiscountEditableStatus =
    isLineItemEditableStatus ||
    PODetails?.po_status === "awaiting_grn" ||
    PODetails?.po_status === "issue_in_invoice_upload" ||
    PODetails?.po_status === "closed" ||
    PODetails?.po_status === "issue_in_grn";
  const isPriceDiscountEditableStatus = isEditModePriceDiscountEditableStatus;

  // Invoice verification: central ops reconciles the scanned vendor invoice against the PO lines
  // before opening the PO for GRN. Outside that step the OCR columns/actions stay hidden.
  const ocrItems = useMemo(
    () => PODetails?.invoice_details?.ocr?.analysis?.items ?? [],
    [PODetails?.invoice_details?.ocr?.analysis?.items],
  );

  const isInvoiceVerification =
    isCentralOps && PODetails?.po_status === "pending_invoice_verification" && ocrItems.length > 0;
  // One SKU split across batches comes as several invoice rows; the PO line takes them together.
  const ocrItemsByVariantId = useMemo(() => {
    const map = new Map<string, IPOInvoiceOcrItem>();
    ocrItems.forEach((item) => {
      if (!item?.variant_id) return;
      const seen = map.get(item.variant_id);
      map.set(item.variant_id, seen ? mergeOcrBatch(seen, item) : item);
    });
    return map;
  }, [ocrItems]);
  const ocrAnalysis = PODetails?.invoice_details?.ocr?.analysis ?? null;
  const poVendorName = PODetails?.primary_purchase_info?.vendor_details?.vendor_name;
  // The scan could not place the invoice's GSTIN, so its product matching is not to be trusted.
  const isOcrVendorUnknown = isInvoiceVerification && Boolean(ocrAnalysis?.vendor_unknown);
  const productInfoWatch = formHook.watch("product_info");
  // Invoice lines with no matching PO line. Unmapped ones (variant_id === null) can only be rejected.
  const ocrExtraItems = useMemo(() => {
    if (!isInvoiceVerification) return [];
    const poVariantIds = new Set((productInfoWatch ?? []).map((item) => item?.variant_id));
    return ocrItems
      .map((item, ocrIndex) => ({ item, ocrIndex, key: `ocr:${ocrIndex}` }))
      .filter(({ item, ocrIndex }) => {
        if (!item?.variant_id) return true;
        if (acceptedExtraRows.includes(ocrIndex)) return false;
        // Once one batch of a SKU is accepted, its other batches stay up for their own accept / reject.
        const isOtherBatchAccepted = ocrItems.some(
          (other, otherIndex) => other?.variant_id === item.variant_id && acceptedExtraRows.includes(otherIndex),
        );
        return isOtherBatchAccepted || !poVariantIds.has(item.variant_id);
      });
  }, [isInvoiceVerification, ocrItems, productInfoWatch, acceptedExtraRows]);
  // Invoice-only lines the backend could map to a Vetic variant lead the table; unmapped ones close it.
  const ocrIdentifiedExtraItems = useMemo(
    () => ocrExtraItems.filter(({ item }) => Boolean(item?.variant_id)),
    [ocrExtraItems],
  );
  const ocrUnidentifiedExtraItems = useMemo(
    () => ocrExtraItems.filter(({ item }) => !item?.variant_id),
    [ocrExtraItems],
  );
  /**
   * Verification reading order: amber (not in invoice) first, then the green lines the scan changed,
   * then the untouched green ones. Outside verification the PO's own order stands.
   * Keeps each line's form index so `product_info.<index>` registrations stay correct.
   */
  const orderedPOFields = useMemo(() => {
    const rows = (POProductsHook?.fields ?? []).map((field, index) => ({ field, index }));
    if (!isInvoiceVerification) return rows;
    const groupOf = ({ field }: { field: (typeof rows)[number]["field"] }) => {
      if (!ocrItemsByVariantId.get(field.variant_id)) {
        // Amber only while undecided — an accepted line turns green and joins the plain green block.
        return ocrDecisions[field.variant_id] === "accepted" ? 2 : 0;
      }
      return ocrOverwrittenFields[field.variant_id] ? 1 : 2; // green with updates, then plain green
    };
    return rows
      .map((row, order) => ({ row, order, group: groupOf(row) }))
      .sort((a, b) => a.group - b.group || a.order - b.order)
      .map(({ row }) => row);
  }, [POProductsHook?.fields, isInvoiceVerification, ocrItemsByVariantId, ocrOverwrittenFields, ocrDecisions]);
  const tableSkeletonRows = POProductsHook?.fields?.length > 0 ? POProductsHook.fields.length : 3;

  const SynthesisePOPayload = (productsInfo: IInventoryPurchaseOrderDraftProductInfo[] = []) => {
    return productsInfo.map((np) => {
      return {
        ...np,
        package_count: toNumber(np.package_count),
        variant_name: np.variant_name,
        variant_id: np.variant_id,
        package_unit: np.package_unit === "" ? null : np.package_unit,
        price_per_package: toNumber(np.price_per_package),
        discount_percentage: np.discount_percentage === 0 ? 0 : toNumber(np.discount_percentage),
        discount_type: np.discount_type,
        total_cost_before_tax: np?.total_cost_before_tax || 0,
        hsn_code: np?.hsn_code || null,
        total_cost_after_tax: np?.total_cost_after_tax || 0,
        total_cost_after_discount: np?.total_cost_after_discount,
      };
    });
  };

  const getCalculationPayload = (productsInfo: IInventoryPurchaseOrderDraftProductInfo[] = []): IPOTaxCalculationFields => {
    const billToClinicId = formHook.getValues("primary_purchase_info.clinic_details.bill_to_clinic_id");
    const vendorBranchId = formHook.getValues("primary_purchase_info.vendor_details.branch_id");
    const shipToClinicId = formHook.getValues("primary_purchase_info.clinic_details.ship_to_clinic_id");

    return {
      branch_id: vendorBranchId || null,
      bill_to_clinic_id: billToClinicId || null,
      ship_to_clinic_id: shipToClinicId || null,
      product_details: SynthesisePOPayload(productsInfo),
    };
  };

  const handleProductCalculations = () => {
    const currentProducts = formHook.getValues("product_info") || [];
    const constructedBody = getCalculationPayload(currentProducts);
    const calculation = calculateProductInfo(constructedBody).then((updatedPOValues) => {
      if (updatedPOValues?.length > 0) {
        updatedPOValues.forEach((po, index) => {
          POProductsHook.update(index, po);
        });
      }
      // Runs on a cell's blur: the edit is saved once its totals are back.
      if (hasUnsavedLineEditRef.current) {
        hasUnsavedLineEditRef.current = false;
        requestVerificationSave();
      }
    });
    if (productCalculationRef) productCalculationRef.current = calculation;
  };

  const handleAddProductItem = (item: IInventoryPurchaseOrderProductSearchResponseData) => {
    const isPresent = POProductsHook.fields.find((f) => f.variant_id === item.vetic_variant_id);
    if (isPresent) {
      toast.error("Product already added.");
      return;
    }
    const nextLineItem: IInventoryPurchaseOrderDraftProductInfo = {
      variant_name: item.variant_name,
      variant_id: item.vetic_variant_id,
      package_count: 1,
      package_unit: item?.unit_type,
      price_per_package: null,
      qty_per_package: item.qty_per_package,
      discount_percentage: 0,
      discount_type: "PERCENTAGE",
      manufacturer: item?.manufacturer ?? {},
      total_cost_before_tax: 0,
      total_cost_after_tax: 0,
      hsn_code: item.hsn_code,
      tax_details: item.tax_details,
      isNew: true,
    };
    const currentProducts = formHook.getValues("product_info") || [];
    const nextProducts = [...currentProducts, nextLineItem];
    calculateProductInfo(getCalculationPayload(nextProducts)).then((calculatedProducts) => {
      if (!calculatedProducts?.length) return;
      POProductsHook.replace(calculatedProducts);
      setNewlyAddedVariants((prev) => [...prev, item.vetic_variant_id]);
      requestVerificationSave();
    });
  };

  /** Writes products into the form with recalculated totals; keeps the edit even if the tax call fails. */
  const applyProducts = (nextProducts: IInventoryPurchaseOrderDraftProductInfo[]) => {
    setIsProductTableLoading(true);

    const derivedProducts = nextProducts.map(withDerivedTotals);
    const calculation = calculateProductInfo(getCalculationPayload(derivedProducts))
      .then((calculated) => {
        POProductsHook.replace(calculated?.length ? calculated : derivedProducts);
        if (!calculated?.length && derivedProducts.length) {
          toast.error("Could not fetch tax — totals were computed locally; run Tax Refresh before saving.");
        }
      })
      .finally(() => setIsProductTableLoading(false));
    if (productCalculationRef) productCalculationRef.current = calculation;
    return calculation;
  };

  // Invoice verification is saved on the user's own actions only — accept / reject, add / remove, Tax
  // Refresh, and a line cell once it loses focus — never for the scan applied on load, so just opening
  // a PO saves nothing. A request is carried out after the next render (so the save sees the new
  // decisions) and once any table recalculation has finished; see the save effect further down.
  const [verificationSaveRequest, setVerificationSaveRequest] = useState(0);
  const requestVerificationSave = () => {
    if (isInvoiceVerification) setVerificationSaveRequest((count) => count + 1);
  };
  // A line cell changed since the last save; its save waits for the blur and the recalculation after it.
  const hasUnsavedLineEditRef = useRef(false);
  useEffect(() => {
    if (!isInvoiceVerification) return;
    // Cell-level writes only: typing, or picking a unit / discount type. Array-wide writes (load,
    // reset, recalculation) arrive as plain "product_info" and are not user edits.
    const subscription = formHook.watch((_values, { name }) => {
      if (name?.startsWith("product_info.")) hasUnsavedLineEditRef.current = true;
    });
    return () => subscription.unsubscribe();
  }, [isInvoiceVerification]);

  // Matched lines take the invoice's values automatically, once per PO load. Each field the scan
  // rewrites is recorded with its previous value so the cell can be marked as AI-changed.
  // Depends on the line count because this effect runs before the parent's formHook.reset() fills
  // product_info on mount; without it the first, empty run is never retried.
  useEffect(() => {
    if (!isInvoiceVerification || !PODetails?.po_id) return;
    if (ocrAppliedForPOIdRef.current === PODetails.po_id) return;
    const currentProducts = formHook.getValues("product_info") ?? [];
    // Rejected lines are saved on the PO but kept out of the form by the parent's reset.
    const savedRejectedLines = (PODetails?.product_info ?? []).filter((line) => line?.ocr_decision === "rejected");
    // Wait for the parent's reset to fill the form, unless every saved line was rejected.
    if (!currentProducts.length && (PODetails?.product_info?.length ?? 0) > savedRejectedLines.length) return;
    ocrAppliedForPOIdRef.current = PODetails.po_id;

    // Decisions saved by an earlier verification session, possibly another user's.
    const savedDecisions: Record<string, "accepted" | "rejected"> = {};
    currentProducts.forEach((line) => {
      if (line?.variant_id && line?.ocr_decision === "accepted") savedDecisions[line.variant_id] = "accepted";
    });
    savedRejectedLines.forEach((line) => {
      if (line?.variant_id) savedDecisions[line.variant_id] = "rejected";
    });
    ocrItems.forEach((item, ocrIndex) => {
      if (item?.rejected) savedDecisions[`ocr:${ocrIndex}`] = "rejected";
    });
    setOcrDecisions(savedDecisions);
    setOcrRejectedLines(savedRejectedLines);
    // The saved lines already hold the scan's values plus the user's edits — applying again would undo them.
    if (PODetails?.ocr_applied) return;

    const overwrittenFields: Record<string, Partial<Record<string, string | number>>> = {};
    const nextProducts = currentProducts.map((poItem) => {
      const ocrItem = poItem?.variant_id ? ocrItemsByVariantId.get(poItem.variant_id) : undefined;
      if (!ocrItem) return poItem;
      const diffs = getOcrDiffs(poItem, ocrItem);
      // The invoice always reports discount as a percentage, so a line carrying a scanned discount
      // must be in percentage mode, otherwise the value would be read as a flat amount.
      const ocrHasDiscount = ocrItem?.discount_percentage !== null && ocrItem?.discount_percentage !== undefined;
      const switchesDiscountUnit = ocrHasDiscount && poItem?.discount_type !== "PERCENTAGE";
      if (!diffs.length && !switchesDiscountUnit) return poItem;

      overwrittenFields[poItem.variant_id] = diffs.reduce(
        (acc, diff) => ({ ...acc, [diff.field]: diff.poValue }),
        (switchesDiscountUnit ? { discount_type: poItem?.discount_type } : {}) as Partial<
          Record<string, string | number>
        >,
      );
      const updated = { ...poItem };
      diffs.forEach((diff) => {
        updated[diff.field] = Number(diff.ocrValue);
      });
      if (switchesDiscountUnit) updated.discount_type = "PERCENTAGE";
      return updated;
    });

    const hasInconsistentTotals = nextProducts.some((line) => {
      const derived = withDerivedTotals(line);
      return (
        Math.abs(
          Number(derived?.total_cost_before_tax ?? 0) - Number(line?.total_cost_before_tax ?? 0),
        ) > OCR_NUMERIC_TOLERANCE
      );
    });

    if (!Object.keys(overwrittenFields).length && !hasInconsistentTotals) return;
    setOcrOverwrittenFields(overwrittenFields);
    applyProducts(nextProducts);
  }, [isInvoiceVerification, PODetails?.po_id, ocrItemsByVariantId, POProductsHook?.fields?.length]);

  // After a Tax Refresh reload: take the refreshed tax data from the server but keep what the user
  // (or the scan) set on each line. applyProducts writes once the calculate API returns, so it lands
  // after the parent's formHook.reset() to the saved PO.
  useEffect(() => {
    const snapshot = linesBeforeTaxRefreshRef.current;
    if (!snapshot || snapshot.poId !== PODetails?.po_id || !isInvoiceVerification) return;
    linesBeforeTaxRefreshRef.current = null;
    const refreshedByVariantId = new Map(
      (PODetails?.product_info ?? []).map((line) => [line?.variant_id, line] as const),
    );
    const mergedLines = snapshot.lines.map((line) => {
      const refreshed = refreshedByVariantId.get(line?.variant_id);
      if (!refreshed) return line;
      return {
        ...refreshed,
        package_count: line.package_count,
        package_unit: line.package_unit,
        price_per_package: line.price_per_package,
        discount_percentage: line.discount_percentage,
        discount_type: line.discount_type,
      };
    });
    applyProducts(mergedLines);
    requestVerificationSave();
  }, [PODetails]);

  /** Accept a PO line the invoice does not list: it stays on the PO. Final — no undo. */
  const handleAcceptMissingLine = (variantId: string) => {
    setOcrDecisions((prev) => ({ ...prev, [variantId]: "accepted" }));
    requestVerificationSave();
  };

  /** Reject a PO line the invoice does not list: it comes off the PO, but stays on screen in red. */
  const handleRejectMissingLine = (index: number, variantId: string) => {
    const currentProducts = formHook.getValues("product_info") ?? [];
    const rejectedLine = currentProducts[index];
    setOcrDecisions((prev) => ({ ...prev, [variantId]: "rejected" }));
    if (rejectedLine) {
      setOcrRejectedLines((prev) => [...prev, rejectedLine]);
    }
    applyProducts(currentProducts.filter((_item, i) => i !== index));
    requestVerificationSave();
  };

  /**
   * Turns one invoice line into a PO line, pulling unit/qty-per-package from the catalogue since the
   * scan does not carry them. Shared by the single-row accept and the accept-all flow.
   */
  /**
   * Builds a PO line straight from the scanned invoice — no catalogue lookup, no tax API. Everything
   * the line needs is on the invoice row; the unit is assumed to be a single pack.
   */
  const buildLineItemFromOcrItem = (ocrItem: IPOInvoiceOcrItem): IInventoryPurchaseOrderDraftProductInfo => {
    const packageCount = Number(ocrItem?.quantity ?? 1) || 1;
    const pricePerPackage = Number(ocrItem?.unit_price ?? 0);
    const discountPercentage = Number(ocrItem?.discount_percentage ?? 0);
    const taxRate = getOcrTaxRate(ocrItem);

    const costBeforeTax = roundToPaise(packageCount * pricePerPackage);
    const discountAmount = roundToPaise((costBeforeTax * discountPercentage) / 100);
    const costAfterDiscount = roundToPaise(costBeforeTax - discountAmount);
    const costAfterTax = roundToPaise(costAfterDiscount * (1 + taxRate / 100));

    return {
      variant_name: ocrItem?.variant_name || ocrItem?.item_name || "",
      variant_id: ocrItem.variant_id,
      package_count: packageCount,
      package_unit: "Pack",
      qty_per_package: 1,
      price_per_package: pricePerPackage,
      discount_percentage: discountPercentage,
      discount_type: "PERCENTAGE",
      manufacturer: {} as IInventoryPurchaseOrderDraftProductInfo["manufacturer"],
      total_cost_before_tax: costBeforeTax,
      total_discount_before_tax: discountAmount,
      total_cost_after_discount: costAfterDiscount,
      total_cost_after_tax: costAfterTax,
      hsn_code: ocrItem?.hsn_code || null,
      tax_details: `${taxRate}%`,
      isNew: true,
    };
  };

  /**
   * Puts each SKU's accepted invoice batches on the PO as one line, their quantities added together:
   * updates the SKU's line when an earlier batch already added it, adds a new line otherwise.
   */
  const withAcceptedExtraLines = (
    products: IInventoryPurchaseOrderDraftProductInfo[],
    variantIds: string[],
    acceptedRows: number[],
  ) => {
    const nextProducts = [...products];
    const addedVariantIds: string[] = [];
    new Set(variantIds).forEach((variantId) => {
      const acceptedBatches = ocrItems.filter(
        (item, ocrIndex) => item?.variant_id === variantId && acceptedRows.includes(ocrIndex),
      );
      if (!acceptedBatches.length) return;
      const line = buildLineItemFromOcrItem(acceptedBatches.reduce(mergeOcrBatch));
      const existingIndex = nextProducts.findIndex((item) => item?.variant_id === variantId);
      if (existingIndex === -1) {
        nextProducts.push(line);
        addedVariantIds.push(variantId);
        return;
      }
      // Keep what the user set on the line (unit, pack size); only the invoice quantities and totals move.
      nextProducts[existingIndex] = {
        ...nextProducts[existingIndex],
        package_count: line.package_count,
        price_per_package: line.price_per_package,
        discount_percentage: line.discount_percentage,
        discount_type: line.discount_type,
        total_cost_before_tax: line.total_cost_before_tax,
        total_discount_before_tax: line.total_discount_before_tax,
        total_cost_after_discount: line.total_cost_after_discount,
        total_cost_after_tax: line.total_cost_after_tax,
      };
    });
    return { nextProducts, addedVariantIds };
  };

  /** Accept one invoice-only row into the PO. Only possible once the backend has mapped a variant. */
  const handleAcceptExtraItem = (ocrItem: IPOInvoiceOcrItem, ocrIndex: number) => {
    if (!ocrItem?.variant_id || acceptedExtraRows.includes(ocrIndex)) return;
    const nextAcceptedRows = [...acceptedExtraRows, ocrIndex];
    const { nextProducts, addedVariantIds } = withAcceptedExtraLines(
      formHook.getValues("product_info") ?? [],
      [ocrItem.variant_id],
      nextAcceptedRows,
    );
    setAcceptedExtraRows(nextAcceptedRows);
    if (addedVariantIds.length) setNewlyAddedVariants((prev) => [...prev, ...addedVariantIds]);
    POProductsHook.replace(nextProducts);
    requestVerificationSave();
  };

  /**
   * Everything the two bulk buttons act on: PO lines the invoice never listed, invoice-only lines
   * split by whether a Vetic variant was identified, and the lines the scan already rewrote.
   * Lines the user has already decided one by one are excluded — those decisions are final.
   */
  const ocrPendingChanges = useMemo(() => {
    if (!isInvoiceVerification) {
      return { missingLines: [], identifiedExtras: [], unidentifiedExtras: [], scanUpdatedCount: 0 };
    }
    const products = productInfoWatch ?? [];
    return {
      missingLines: products.filter(
        (item) => item?.variant_id && !ocrItemsByVariantId.get(item.variant_id) && !ocrDecisions[item.variant_id],
      ),
      identifiedExtras: ocrIdentifiedExtraItems.filter(({ key }) => !ocrDecisions[key]),
      unidentifiedExtras: ocrUnidentifiedExtraItems.filter(({ key }) => !ocrDecisions[key]),
      scanUpdatedCount: products.filter((item) => item?.variant_id && ocrOverwrittenFields[item.variant_id]).length,
    };
  }, [
    isInvoiceVerification,
    productInfoWatch,
    ocrItemsByVariantId,
    ocrDecisions,
    ocrIdentifiedExtraItems,
    ocrUnidentifiedExtraItems,
    ocrOverwrittenFields,
  ]);

  const hasPendingOcrChanges =
    ocrPendingChanges.missingLines.length > 0 ||
    ocrPendingChanges.identifiedExtras.length > 0 ||
    ocrPendingChanges.unidentifiedExtras.length > 0 ||
    ocrPendingChanges.scanUpdatedCount > 0;

  /** The bullet list shown in the confirmation popup — the same wording the flow was specced with. */
  const ocrBulkChangeSummary = (decision: OcrBulkDecision) => {
    const { missingLines, identifiedExtras, unidentifiedExtras, scanUpdatedCount } = ocrPendingChanges;
    const changes: string[] = [];
    if (decision === "accept") {
      if (missingLines.length) changes.push(`Remove ${missingLines.length} item(s) not in the invoice from the PO`);
      if (identifiedExtras.length)
        changes.push(`Add ${identifiedExtras.length} extra item(s) from the invoice (Vetic variant identified)`);
      if (unidentifiedExtras.length)
        changes.push(`Ignore ${unidentifiedExtras.length} invoice item(s) with no Vetic variant identified`);
      if (scanUpdatedCount)
        changes.push(`Keep the invoice values already applied to ${scanUpdatedCount} matched item(s)`);
      return changes;
    }
    if (missingLines.length) changes.push(`Keep ${missingLines.length} item(s) not in the invoice on the PO`);
    if (identifiedExtras.length)
      changes.push(`Do not add ${identifiedExtras.length} extra item(s) from the invoice (Vetic variant identified)`);
    if (unidentifiedExtras.length)
      changes.push(`Ignore ${unidentifiedExtras.length} invoice item(s) with no Vetic variant identified`);
    if (scanUpdatedCount) changes.push(`Restore the PO's own values on ${scanUpdatedCount} matched item(s)`);
    return changes;
  };

  /** Accept the invoice wholesale: drop what it does not list, pull in what it adds, ignore the unmapped. */
  const handleAcceptAllChanges = async () => {
    const currentProducts = formHook.getValues("product_info") ?? [];
    const removedVariantIds = new Set(ocrPendingChanges.missingLines.map((line) => line?.variant_id));
    const removedLines = currentProducts.filter((item) => removedVariantIds.has(item?.variant_id));
    const keptProducts = currentProducts.filter((item) => !removedVariantIds.has(item?.variant_id));

    setIsProductTableLoading(true);
    try {
      const nextAcceptedRows = [
        ...acceptedExtraRows,
        ...ocrPendingChanges.identifiedExtras.map(({ ocrIndex }) => ocrIndex),
      ];
      const { nextProducts, addedVariantIds } = withAcceptedExtraLines(
        keptProducts,
        ocrPendingChanges.identifiedExtras.map(({ item }) => item.variant_id),
        nextAcceptedRows,
      );
      setAcceptedExtraRows(nextAcceptedRows);
      setOcrDecisions((prev) => {
        const next = { ...prev };
        removedVariantIds.forEach((variantId) => {
          next[variantId] = "rejected";
        });
        ocrPendingChanges.unidentifiedExtras.forEach(({ key }) => {
          next[key] = "rejected";
        });
        return next;
      });
      if (removedLines.length) setOcrRejectedLines((prev) => [...prev, ...removedLines]);
      if (addedVariantIds.length) setNewlyAddedVariants((prev) => [...prev, ...addedVariantIds]);
      await applyProducts(nextProducts);
      requestVerificationSave();
    } finally {
      setIsProductTableLoading(false);
    }
  };

  /** Reject the invoice wholesale: the PO stands as it was, including the values the scan overwrote. */
  const handleRejectAllChanges = async () => {
    const currentProducts = formHook.getValues("product_info") ?? [];
    const restoredProducts = currentProducts.map((item) => {
      const previousValues = item?.variant_id ? ocrOverwrittenFields[item.variant_id] : undefined;
      if (!previousValues) return item;
      const restored = { ...item };
      Object.entries(previousValues).forEach(([field, value]) => {
        // "—" is the placeholder for a field the PO never carried — leave such a field untouched.
        if (value === "—" || value === undefined) return;
        restored[field] = value;
      });
      return restored;
    });

    setOcrDecisions((prev) => {
      const next = { ...prev };
      ocrPendingChanges.missingLines.forEach((line) => {
        next[line.variant_id] = "accepted";
      });
      [...ocrPendingChanges.identifiedExtras, ...ocrPendingChanges.unidentifiedExtras].forEach(({ key }) => {
        next[key] = "rejected";
      });
      return next;
    });
    setOcrOverwrittenFields({});
    await applyProducts(restoredProducts);
    requestVerificationSave();
  };

  const applyBulkDecision = async (decision: OcrBulkDecision) => {
    if (decision === "accept") {
      await handleAcceptAllChanges();
      toast.success("All invoice changes accepted.");
      return;
    }
    await handleRejectAllChanges();
    toast.success("All invoice changes rejected.");
  };

  /**
   * Accepted lines the invoice does not list (still on the PO), and the invoice lines rejected from
   * it — by index, since an unmapped invoice line has no variant to name it by.
   */
  const getSaveMarkers = () => {
    const poVariantIds = new Set((formHook.getValues("product_info") ?? []).map((line) => line?.variant_id));
    // One record per variant; a line added back to the PO is no longer rejected.
    const rejectedLinesByVariantId = new Map(
      ocrRejectedLines
        .filter((line) => line?.variant_id && !poVariantIds.has(line.variant_id))
        .map((line) => [line.variant_id, line] as const),
    );
    return {
      rejectedLines: [...rejectedLinesByVariantId.values()],
      acceptedVariantIds: Object.keys(ocrDecisions).filter(
        (variantId) => ocrDecisions[variantId] === "accepted" && poVariantIds.has(variantId),
      ),
      rejectedOcrItemIndexes: ocrItems.flatMap((item, ocrIndex) =>
        ocrDecisions[`ocr:${ocrIndex}`] === "rejected" && !poVariantIds.has(item?.variant_id)
          ? [ocrIndex]
          : [],
      ),
    };
  };

  const handleConfirmBulkDecision = async (decision: OcrBulkDecision) => {
    setOcrBulkDecision(null);
    await applyBulkDecision(decision);
  };

  // Kept current on every render so the submit button always reads the live decision state.
  useEffect(() => {
    if (!ocrGateRef) return;
    ocrGateRef.current = isInvoiceVerification
      ? {
          hasPendingChanges: hasPendingOcrChanges,
          changeSummary: ocrBulkChangeSummary,
          applyBulkDecision,
          getSaveMarkers,
        }
      : null;
    return () => {
      ocrGateRef.current = null;
    };
  });

  // Carries out a requested verification save. Declared after the gate effect so the save reads this
  // render's decisions. Saves never overlap: one asked for mid-flight runs once the current one ends,
  // with the then-current PO, so an older save can never land after a newer one.
  const saveInvoiceVerificationRef = useRef(onInvoiceVerificationChange);
  saveInvoiceVerificationRef.current = onInvoiceVerificationChange;
  const handledVerificationSaveRef = useRef(0);
  const isVerificationSavingRef = useRef(false);
  const isVerificationSaveQueuedRef = useRef(false);
  const runVerificationSave = () => {
    if (isVerificationSavingRef.current) {
      isVerificationSaveQueuedRef.current = true;
      return;
    }
    isVerificationSavingRef.current = true;
    Promise.resolve(saveInvoiceVerificationRef.current?.()).finally(() => {
      isVerificationSavingRef.current = false;
      if (isVerificationSaveQueuedRef.current) {
        isVerificationSaveQueuedRef.current = false;
        runVerificationSave();
      }
    });
  };
  useEffect(() => {
    if (verificationSaveRequest === handledVerificationSaveRef.current || isProductTableLoading) return;
    handledVerificationSaveRef.current = verificationSaveRequest;
    runVerificationSave();
  }, [verificationSaveRequest, isProductTableLoading]);

  const isInputDisable = () => {
    const clinicId = formHook.getValues("primary_purchase_info.clinic_details.bill_to_clinic_id") || "";
    const vendorBrandId = formHook.getValues("primary_purchase_info.vendor_details.branch_id") || "";
    if (clinicId === "" || vendorBrandId === "") {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (!userPermissions || !PODetails) return;
    if (!isCentralOps) {
      setEditPermission(false);
      return;
    }
    setEditPermission(true);
  }, [PODetails, userPermissions]);

  useEffect(() => {
    fetchUnitListAPI();
    fetchDiscountListAPI();
  }, []);

  const calculateSummary = () => {
    const productInfo = formHook.watch("product_info");
    const totals = productInfo?.reduce(
      (acc, item) => {
        if (!item) return acc;
        acc.total_cost_before_tax += item?.total_cost_before_tax || 0;
        acc.total_cost_after_tax += item?.total_cost_after_tax || 0;
        acc.total_cost_after_discount += item?.total_cost_after_discount || 0;
        const taxRate = parseFloat(item?.tax_details?.replace("%", "")) / 100;
        if (!isNaN(taxRate)) {
          const taxAmount = item?.total_cost_after_discount * taxRate;
          if (item?.tax_details === "5%" || item?.tax_details === "5.0%") acc.tax_5 += taxAmount;
          else if (item?.tax_details === "12%" || item?.tax_details === "12.0%") acc.tax_12 += taxAmount;
          else if (item?.tax_details === "18%" || item?.tax_details === "18.0%") acc.tax_18 += taxAmount;
        }
        return acc;
      },
      {
        discounted_amount: 0,
        total_cost_before_tax: 0,
        total_cost_after_tax: 0,
        total_cost_after_discount: 0,
        tax_5: 0,
        tax_12: 0,
        tax_18: 0,
        total_tax: 0,
      },
    );
    if (!totals) {
      return {
        taxable_amount: 0,
        discounted_amount: 0,
        net_cost: 0,
        tax_5: "0.00",
        tax_12: "0.00",
        tax_18: "0.00",
        total_tax: "0.00",
        total_cost_after_tax: 0,
      };
    }
    totals.discounted_amount = (totals?.total_cost_before_tax || 0) - (totals?.total_cost_after_discount || 0);
    totals.total_tax = (totals?.tax_5 || 0) + (totals?.tax_12 || 0) + (totals?.tax_18 || 0);
    return {
      taxable_amount: totals.total_cost_before_tax || 0,
      discounted_amount: totals.discounted_amount || 0,
      net_cost: totals.total_cost_after_discount || 0,
      tax_5: totals.tax_5.toFixed(2),
      tax_12: totals.tax_12.toFixed(2),
      tax_18: totals.tax_18.toFixed(2),
      total_tax: totals.total_tax.toFixed(2),
      total_cost_after_tax: totals.total_cost_after_tax || 0,
    };
  };

  const handleFetchTax = async () => {
    setIsUpdatingTax(true);
    setIsProductTableLoading(true);

    const constructedBody = {
      po_id: PODetails?.po_id,
      ship_to_clinic_id: formHook.getValues("primary_purchase_info.clinic_details.ship_to_clinic_id"),
      bill_to_clinic_id: formHook.getValues("primary_purchase_info.clinic_details.bill_to_clinic_id"),
      vendor_id: formHook.getValues("primary_purchase_info.vendor_details.vendor_id"),
      vendor_branchId: formHook.getValues("primary_purchase_info.vendor_details.branch_id"),
      vendor_branch_name: formHook.getValues("primary_purchase_info.vendor_details.branch_name")
    };
    try {
      const response = await fetchUpdatedTaxDetails(constructedBody);
      if (response?.status && PODetails?.po_id) {
        if (isInvoiceVerification) {
          linesBeforeTaxRefreshRef.current = {
            poId: PODetails.po_id,
            lines: formHook.getValues("product_info") ?? [],
          };
        }
        await fetchPODetails(PODetails?.po_id);
        toast.success("Tax details Updated successfully.");
      }
    } finally {
      setIsUpdatingTax(false);
      setIsProductTableLoading(false);
    }
  };

  /** One invoice-only line: billed by the vendor but absent from the PO. */
  const renderOcrExtraRow = ({ item, key, ocrIndex }: { item: IPOInvoiceOcrItem; key: string; ocrIndex: number }) => {
    const isExtraRejected = ocrDecisions[key] === "rejected";
    const extraStatus: OcrLineStatus = isExtraRejected ? "rejected" : "extra";
    // Preview the exact line accepting would create, so the row reads like the PO lines around it.
    const preview = buildLineItemFromOcrItem(item);
    return (
      <tr key={key} className={OCR_ROW_TINTS[extraStatus].row}>
        <td className={`sticky left-0 z-10 px-4 py-2 text-gray-400 ${OCR_ROW_TINTS[extraStatus].stickyCell}`}>
          —
        </td>
        <td className={`sticky left-0 z-10 px-4 py-2 ${OCR_ROW_TINTS[extraStatus].stickyCell}`}>
          <div className={`break-words w-52 ${isExtraRejected ? "line-through" : ""}`}>
            {item?.variant_name || item?.item_name || "Unnamed invoice item"}
          </div>
          {item?.variant_name && item?.item_name && (
            <p className="mt-0.5 text-[10px] text-gray-500 break-words w-52">Invoice: {item.item_name}</p>
          )}
          <OcrStatusPill status={extraStatus} />
          {!item?.variant_id && !isExtraRejected && (
            <p className="mt-0.5 text-[10px] text-gray-500">Not mapped to a Vetic product</p>
          )}
        </td>
        <td className="px-4 py-2 text-xs 2xl:text-sm">
          <span className="flex items-center gap-2">
            <span>{preview.package_count}</span>
            <span className="text-gray-500">{preview.package_unit}</span>
          </span>
        </td>
        <td className="px-4 py-2 text-xs 2xl:text-sm">{preview.qty_per_package}</td>
        <td className="px-4 py-2 text-xs 2xl:text-sm">{preview.price_per_package}</td>
        <td className="px-4 py-2 text-xs 2xl:text-sm">{preview.total_cost_before_tax}</td>
        <td className="px-4 py-2 text-xs 2xl:text-sm">{preview.discount_percentage}</td>
        <td className="px-4 py-2 text-xs 2xl:text-sm">
          <p className="flex justify-center items-center gap-x-1">{preview.total_cost_after_discount}</p>
        </td>
        <td className="px-4 py-2 text-xs 2xl:text-sm">{preview.hsn_code ?? ""}</td>
        <td className="px-4 py-2 text-xs 2xl:text-sm">{preview.tax_details}</td>
        <td className="px-4 py-2 text-xs 2xl:text-sm">
          <p className="flex justify-center items-center gap-x-1">{preview.total_cost_after_tax}</p>
        </td>
        <td className="px-4 py-2">
          {!isExtraRejected && (
            <div className="flex justify-center items-center gap-1.5">
              {/* Without a Vetic variant there is nothing to add to the PO — reject is the only move. */}
              {item?.variant_id && (
                <OcrIconButton
                  title="Accept — add this invoiced item to the PO"
                  variant="accept"
                  disabled={isProductTableLoading}
                  onClick={() => handleAcceptExtraItem(item, ocrIndex)}
                >
                  <CheckIcon className="w-4 h-4" />
                </OcrIconButton>
              )}
              <OcrIconButton
                title="Reject — leave this item off the PO"
                variant="reject"
                disabled={isProductTableLoading}
                onClick={() => {
                  setOcrDecisions((prev) => ({ ...prev, [key]: "rejected" }));
                  requestVerificationSave();
                }}
              >
                <TrashIcon className="w-4 h-4" />
              </OcrIconButton>
            </div>
          )}
        </td>
      </tr>
    );
  };

  return (
    isObject(PODetails) &&
    isObject(userPermissions) &&
    Object.keys(userPermissions)?.length > 0 &&
    Object.keys(PODetails)?.length > 0 && (
      <div>
        {PODetails?.po_status === "issue_in_grn" && (
          <AlertError
            title="Issue In GRN"
            description={
              <div className="text-xs">
                <p className="font-semibold text-xs 2xl:text-sm">
                  Issue Raised : <span className="font-normal">{PODetails?.raised_error_reason}</span>{" "}
                </p>
              </div>
            }
          />
        )}


        {isOcrVendorUnknown && (
          <div className="mt-3">
            <AlertError
              title="Check the vendor on this invoice"
              description={
                <div className="text-xs 2xl:text-sm">
                  <p>
                    This vendor&apos;s GSTIN is not one we hold, so the matched products need checking before you open
                    this PO for GRN.
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2">
                    <p>
                      <span className="font-semibold">Invoice vendor:</span>{" "}
                      {ocrAnalysis?.vendor_name || "Could not be read"}
                      {ocrAnalysis?.gstin ? ` · GSTIN ${ocrAnalysis.gstin}` : ""}
                    </p>
                    <p>
                      <span className="font-semibold">PO vendor:</span> {poVendorName || "—"}
                    </p>
                  </div>
                </div>
              }
            />
          </div>
        )}

        <div className="flex justify-between items-center">
          <h2 className="mt-4 mb-2 font-semibold text-sm text-[#1F1F23]">Product Information</h2>
          {editPermission && isCreationEditableStatus && (
            <div className="flex gap-3">
              {POProductsHook?.fields?.length > 0 && (
                <div className="relative flex gap-1">
                  <button
                    className="flex items-center font-medium text-blue-600 text-xs hover:text-blue-700"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      formHook.setValue("product_info", []);
                      bulkClearApi({ po_id: PODetails?.po_id });
                    }}
                  >
                    Clear Items
                  </button>
                  <div
                    className="flex items-center"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <RiInformationLine className="text-blue-500 cursor-pointer" />
                    {showTooltip && (
                      <span className="bottom-full left-1/2 absolute bg-black px-2 py-1 rounded-md w-52 text-white transform -translate-x-1/2 text-xs 2xl:text-sm">
                        To edit Vendor & Clinic details you must clear all items first
                      </span>
                    )}
                  </div>
                </div>
              )}
              <button
                className={`inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90 ${
                  !formHook.watch("primary_purchase_info.vendor_details.branch_id") ||
                  !formHook.watch("primary_purchase_info.clinic_details.bill_to_clinic_id") ||
                  !formHook.watch("primary_purchase_info.clinic_details.ship_to_clinic_id")
                    ? "opacity-50"
                    : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  formHook.trigger().then((res) => {
                    if (res) {
                      bulkUpload();
                    }
                  });
                }}
              >
                <FiUpload />
                Bulk Upload
              </button>
            </div>
          )}
        </div>
        {/* {(!formHook.watch("product_info") || formHook.watch("product_info")?.length == 0) && (
          <div className="flex gap-2 mt-2 font-semibold text-[rgb(184,132,47)]">
            <RiInformationLine />
            <p className="text-xs 2xl:text-sm">
              Vendor & Clinic details cannot be edited after adding items in the PO
            </p>
          </div>
        )} */}
        <div className="flex justify-end items-center mb-4">
          {editPermission && isEditableProductStatus && isCentralOps && (
            <div className="shadow-sm mt-1 pb-3 w-full">
              <div className="">
                <InventoryProductSearchComponent
                  variant_category_id={formHook.watch("primary_purchase_info.vms_category_details.variant_category_id")}
                  type={formHook.watch("primary_purchase_info.type")}
                  clinic_id={formHook.watch("primary_purchase_info.clinic_details.ship_to_clinic_id")}
                  onSelectItem={(item) => {
                    handleAddProductItem(item);
                  }}
                  containerClassName="mt-2 w-1/2 shadow-none"
                  itemActiveClassName="bg-orange-500 text-white"
                  inputContainerClassName="w-full"
                  flexibleAdd={false}
                  isInputEnabled={isInputDisable()}
                />
              </div>
            </div>
          )}

          {isCentralOps && PODetails?.po_status !== "sent_to_finance" &&
            <button
              onClick={() => handleFetchTax()}
              className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
              type="button"
              disabled={isUpdatingTax}
              style={{ width: `${FETCH_TAX_BUTTON_WIDTH}px`, height: `${FETCH_TAX_BUTTON_HEIGHT}px` }}
            >
              {isUpdatingTax ? (
                <CircleSpinnerLoader h={`${FETCH_TAX_BUTTON_HEIGHT}`} w={`${FETCH_TAX_BUTTON_WIDTH}`} />
              ) : (
                "Tax Refresh"
              )}
            </button>
          }
        </div >
        <div className={`max-w-full border border-gray-200 rounded-lg overflow-x-scroll`}>
          {isProductTableLoading ? (
            <TableSkeletonComponent
              columnLength={12}
              rowLength={tableSkeletonRows}
              containerClassName="!mt-0 !h-auto !overflow-hidden"
            />
          ) : (
            (POProductsHook?.fields?.length > 0 || ocrExtraItems.length > 0 || ocrRejectedLines.length > 0) && (
            <table className="min-w-max  border-collapse ">
              <thead>
                <tr className="bg-gray-100 text-left text-xs 2xl:text-sm font-medium sticky top-0 z-20">
                  <th className="sticky top-0 left-0 z-20 font-medium text-black text-xs 2xl:text-sm px-4 py-2 bg-gray-200">
                    S.No.
                  </th>
                  <th className="sticky top-0 left-0 z-20 font-medium text-black text-xs 2xl:text-sm px-4 py-2 bg-gray-200">
                    Product Name
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    No. of Packages & Unit
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    Qty/pkg
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    Cost Price/pkg
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    Total Cost before Tax
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    Discount (%)
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    Discounted Cost Before Tax
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    HSN Code
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    Total Tax
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    Total Cost After Tax
                  </th>
                  <th className="sticky top-0 font-medium text-black text-xs 2xl:text-sm px-4 py-2">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="text-xs 2xl:text-sm">
                {/* Invoice-only lines already mapped to a Vetic product lead the verification list. */}
                {ocrIdentifiedExtraItems.map(renderOcrExtraRow)}
                {orderedPOFields.map(({ field, index }, displayIndex) => {
                  const isEditable = newlyAddedVariants.includes(field.variant_id);
                  const isGrnDone = Boolean(field?.is_grn_done);
                  const ocrItem = isInvoiceVerification ? ocrItemsByVariantId.get(field.variant_id) : undefined;
                  const isMissingInInvoice = isInvoiceVerification && !ocrItem;
                  const ocrDecision = ocrDecisions[field.variant_id];
                  const ocrChangedFields = ocrOverwrittenFields[field.variant_id];
                  const ocrLineStatus: OcrLineStatus | null = !isInvoiceVerification
                    ? null
                    : ocrItem
                      ? "matched"
                      : ocrDecision === "accepted"
                        ? "accepted"
                        : "missing";
                  // Accept / reject only while the line is still undecided.
                  const isAwaitingOcrDecision = isMissingInInvoice && !ocrDecision;
                  const ocrRowClassName = ocrLineStatus ? OCR_ROW_TINTS[ocrLineStatus].row : "odd:bg-white even:bg-gray-50";
                  const ocrStickyCellClassName = ocrLineStatus ? OCR_ROW_TINTS[ocrLineStatus].stickyCell : "bg-gray-100";
                  /** The PO's pre-invoice value for a field, shown under the input the scan replaced. */
                  const ocrReplacedValueFor = (poField: string) => ocrChangedFields?.[poField];
                  const isLineItemQuantityEditable = (isLineItemEditableStatus || isEditable) && !isGrnDone;
                  const isPackageUnitEditable = (isCreationEditableStatus || isEditable) && !isGrnDone;
                  const isPriceAndDiscountEditable = (isPriceDiscountEditableStatus || isEditable)
                  const canDeleteLineItem = !isGrnDone && (isLineItemEditableStatus || isEditable);
                  return (
                    <tr key={field.id} className={ocrRowClassName}>
                      <td className={`sticky left-0 z-10 px-4 py-2 ${ocrStickyCellClassName}`}>
                        <div>{displayIndex + 1}.</div>
                      </td>
                      <td className={`sticky left-0 z-10 px-4 py-2 ${ocrStickyCellClassName}`}>
                        <div
                          data-tooltip-id="user_detail_name"
                          className="break-words w-52"
                          data-tooltip-content={formHook.getValues(`product_info.${index}.variant_name` as const)}
                        >
                          {formHook.getValues(`product_info.${index}.variant_name` as const)}
                        </div>
                        {ocrLineStatus && <OcrStatusPill status={ocrLineStatus} />}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {isLineItemQuantityEditable ? (
                              <Input
                                disabled={!editPermission}
                                className="shadow-none w-20"
                                errorMessages={[forOnlyNumberWithoutZero?.errors, { message: "", type: "required" }]}
                                fieldError={
                                  formHook?.formState?.errors?.product_info
                                    ? formHook?.formState?.errors?.product_info[index]
                                      ? formHook?.formState?.errors?.product_info[index]?.package_count
                                      : null
                                    : null
                                }
                                register={formHook.register(`product_info.${index}.package_count` as const, {
                                  required: true,
                                  ...forOnlyNumberWithoutZero?.validations,
                                  onBlur: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (e.target.value !== "0") {
                                      handleProductCalculations();
                                    }
                                  },
                                })}
                              />
                            ) : (
                              <p className="text-xs 2xl:text-sm">
                                {formHook.getValues(`product_info.${index}.package_count` as const)}
                              </p>
                            )}
                            {ocrReplacedValueFor("package_count") !== undefined && (
                              <OcrAiChangedValue previousValue={ocrReplacedValueFor("package_count")} />
                            )}
                          </div>
                          <div className="w-24">
                            {isPackageUnitEditable ? (
                              <SearchableSelectMenu
                                errorMessages={[{ message: "", type: "required" }]}
                                onSelectItem={(item) => {
                                  if (item) {
                                    formHook.setValue(`product_info.${index}.package_unit` as const, item.title);
                                    // A unit pick changes no totals, so there is no recalculation to wait for.
                                    hasUnsavedLineEditRef.current = false;
                                    requestVerificationSave();
                                  }
                                  formHook.clearErrors(`product_info.${index}.package_unit`);
                                }}
                                dropdownPosition="bottom"
                                fieldError={
                                  formHook?.formState?.errors?.product_info
                                    ? formHook?.formState?.errors?.product_info[index]
                                      ? formHook?.formState?.errors?.product_info[index]?.package_unit
                                      : null
                                    : null
                                }
                                register={formHook.register(`product_info.${index}.package_unit` as const, {
                                  required: true,
                                })}
                                selectItems={unitOptions}
                                placeholder="Unit"
                                showTooltips={false}
                                showTypedErrors={false}
                                showDropdownIcon={true}
                                defaultSelected={
                                  unitOptions?.filter(
                                    (ci) =>
                                      ci.title?.toLowerCase()?.trim() ===
                                      formHook.watch(`product_info.${index}.package_unit`)?.toLowerCase()?.trim(),
                                  )[0]
                                }
                                className="flex items-center pt-2 text-gray-800 w-24"
                                containerClassName="w-full"
                              />
                            ) : (
                              <span>{formHook.getValues(`product_info.${index}.package_unit` as const)}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {" "}
                        <p className="text-xs 2xl:text-sm">
                          {formHook.getValues(`product_info.${index}.qty_per_package` as const)}
                        </p>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          {isPriceAndDiscountEditable ? (
                            <Input
                              disabled={!editPermission}
                              placeholder="Add CP/kgs"
                              errorMessages={[forNumericWithDecimal?.errors, { message: "", type: "required" }]}
                              fieldError={
                                formHook?.formState?.errors?.product_info
                                  ? formHook?.formState?.errors?.product_info[index]
                                    ? formHook?.formState?.errors?.product_info[index]?.price_per_package
                                    : null
                                  : null
                              }
                              className="w-20"
                              register={formHook.register(`product_info.${index}.price_per_package` as const, {
                                required: true,
                                ...forNumericWithDecimal?.validations,
                                onBlur: (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (e.target.value !== "0") {
                                    handleProductCalculations();
                                  }
                                },
                              })}
                            />
                          ) : (
                            <p className="flex justify-center items-center gap-x-1 font-semibold text-xs 2xl:text-sm">
                              {formHook.getValues(`product_info.${index}.price_per_package` as const)}
                            </p>
                          )}
                          {ocrReplacedValueFor("price_per_package") !== undefined && (
                            <OcrAiChangedValue previousValue={ocrReplacedValueFor("price_per_package")} />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {" "}
                        <p className="flex font-normal items-center gap-x-1 text-xs 2xl:text-sm">
                          {formHook.getValues(`product_info.${index}.total_cost_before_tax` as const)}
                        </p>
                      </td>
                      <td className="px-4 py-2">
                        {" "}
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {isPriceAndDiscountEditable ? (
                              <Input
                                disabled={!editPermission}
                                placeholder="Add Discount"
                                className="w-20"
                                max={100}
                                errorMessages={[
                                  forAllAcceptWithOutStartWithDotAndSpace?.errors,
                                  { message: "", type: "required" },
                                ]}
                                fieldError={
                                  formHook?.formState?.errors?.product_info
                                    ? formHook?.formState?.errors?.product_info[index]
                                      ? formHook?.formState?.errors?.product_info[index]?.discount_percentage
                                      : null
                                    : null
                                }
                                register={formHook.register(`product_info.${index}.discount_percentage` as const, {
                                  required: false,
                                  ...forAllAcceptWithOutStartWithDotAndSpace?.validations,
                                  onBlur: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (e.target.value !== "0") {
                                      handleProductCalculations();
                                    }
                                  },
                                })}
                              />
                            ) : (
                              <p className="text-xs 2xl:text-sm">
                                {formHook.getValues(`product_info.${index}.discount_percentage` as const)}
                              </p>
                            )}
                            {ocrReplacedValueFor("discount_percentage") !== undefined && (
                              <OcrAiChangedValue previousValue={ocrReplacedValueFor("discount_percentage")} />
                            )}
                          </div>
                          <div className="w-20">
                            {isPriceAndDiscountEditable ? (
                              <SearchableSelectMenu
                                errorMessages={[{ message: "", type: "required" }]}
                                dropdownPosition="bottom"
                                onSelectItem={(item) => {
                                  if (item) {
                                    formHook.setValue(`product_info.${index}.discount_type` as const, item.id);
                                    handleProductCalculations();
                                  }
                                  formHook.clearErrors(`product_info.${index}.discount_type`);
                                }}
                                fieldError={
                                  formHook?.formState?.errors?.product_info
                                    ? formHook?.formState?.errors?.product_info[index]
                                      ? formHook?.formState?.errors?.product_info[index]?.discount_type
                                      : null
                                    : null
                                }
                                register={formHook.register(`product_info.${index}.discount_type` as const, {
                                  required: true,
                                })}
                                selectItems={discountOptions}
                                placeholder="Discount"
                                showTooltips={false}
                                showTypedErrors={false}
                                showDropdownIcon={true}
                                defaultSelected={
                                  discountOptions?.filter(
                                    (ci) => ci.id === formHook.watch(`product_info.${index}.discount_type` as const),
                                  )[0]
                                }
                                className="flex items-center pt-2 text-gray-800"
                                containerClassName="w-full"
                              />
                            ) : (
                              <span className="ml-2 text-xs 2xl:text-sm">
                                {field.discount_type === "PERCENTAGE" ? "%" : "₹"}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <p className="font-normal text-black flex justify-center items-center gap-x-1 text-xs 2xl:text-sm">
                          {formHook.getValues(`product_info.${index}.total_cost_after_discount` as const)}
                        </p>
                      </td>
                      <td className="px-4 py-2">
                        <p className="text-xs 2xl:text-sm">
                          {formHook.getValues(`product_info.${index}.hsn_code` as const)}
                        </p>
                      </td>
                      <td className="px-4 py-2">
                        <p className="text-xs 2xl:text-sm">{field?.tax_details}</p>
                      </td>
                      <td className="px-4 py-2">
                        {" "}
                        <p className="flex justify-center items-center gap-x-1 font-normal text-xs 2xl:text-sm">
                          {formHook.getValues(`product_info.${index}.total_cost_after_tax` as const)}
                        </p>
                      </td>
                      <td className="px-4 py-2">
                        {" "}
                        <div className="flex justify-center items-center gap-1.5">
                          {isAwaitingOcrDecision && (
                            <>
                              <OcrIconButton
                                title="Accept — keep this line on the PO"
                                variant="accept"
                                disabled={isProductTableLoading}
                                onClick={() => handleAcceptMissingLine(field.variant_id)}
                              >
                                <CheckIcon className="w-4 h-4" />
                              </OcrIconButton>
                              <OcrIconButton
                                title="Reject — remove this line from the PO"
                                variant="reject"
                                disabled={isProductTableLoading}
                                onClick={() => handleRejectMissingLine(index, field.variant_id)}
                              >
                                <TrashIcon className="w-4 h-4" />
                              </OcrIconButton>
                            </>
                          )}
                        {/* A line still awaiting an invoice decision offers accept/reject only — delete comes back once decided. */}
                        {editPermission &&
                          isCentralOps &&
                          canDeleteLineItem &&
                          !isAwaitingOcrDecision && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                requestVerificationSave();
                                const variantId = field?.variant_id;
                                if (newlyAddedVariants.includes(variantId)) {
                                  POProductsHook.remove(index);
                                  setNewlyAddedVariants((prev) => prev.filter((id) => id !== variantId));
                                  return;
                                }
                                if (PODetails?.po_status === "awaiting_grn") {
                                  deletePOAwaitingGRN({
                                    po_id: PODetails?.po_id,
                                    variant_id: variantId,
                                  })
                                    .then((res) => {
                                      if (res?.status) {
                                        toast.success("Product deleted successfully.");
                                        POProductsHook.remove(index);
                                        setIsProductTableLoading(true);
                                        fetchPODetails(PODetails?.po_id)
                                          .then(() => {
                                            console.log("PO Details refreshed after deletion");
                                          })
                                          .catch(() => toast.error("Failed to refresh PO details."))
                                          .finally(() => setIsProductTableLoading(false));
                                      } else {
                                        toast.error(res?.message || "Failed to delete product.");
                                      }
                                    })
                                    .catch(() => toast.error("Something went wrong while deleting."));
                                } else {
                                  POProductsHook.remove(index);
                                }
                              }}
                            >
                              <TrashIcon className="w-4 h-4 text-red-500 cursor-pointer" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* PO lines the user rejected: off the PO, kept on screen as a record of the decision. */}
                {ocrRejectedLines.map((line) => (
                  <tr key={`rejected:${line?.variant_id}`} className={OCR_ROW_TINTS.rejected.row}>
                    <td className={`sticky left-0 z-10 px-4 py-2 text-gray-400 ${OCR_ROW_TINTS.rejected.stickyCell}`}>
                      —
                    </td>
                    <td className={`sticky left-0 z-10 px-4 py-2 ${OCR_ROW_TINTS.rejected.stickyCell}`}>
                      <div className="break-words w-52 line-through">{line?.variant_name}</div>
                      <OcrStatusPill status="rejected" />
                    </td>
                    <td className="px-4 py-2 text-xs 2xl:text-sm">{line?.package_count}</td>
                    <td className="px-4 py-2 text-xs 2xl:text-sm">{line?.qty_per_package}</td>
                    <td className="px-4 py-2 text-xs 2xl:text-sm">{line?.price_per_package}</td>
                    <td className="px-4 py-2" />
                    <td className="px-4 py-2 text-xs 2xl:text-sm">{line?.discount_percentage}</td>
                    <td className="px-4 py-2" />
                    <td className="px-4 py-2 text-xs 2xl:text-sm">{line?.hsn_code}</td>
                    <td className="px-4 py-2 text-xs 2xl:text-sm">{line?.tax_details}</td>
                    <td className="px-4 py-2" />
                    <td className="px-4 py-2" />
                  </tr>
                ))}
                {/* Invoice lines with no Vetic product mapped — they close out the table. */}
                {ocrUnidentifiedExtraItems.map(renderOcrExtraRow)}
              </tbody>
            </table>
            )
          )}
        </div>
        {/* One decision for the whole invoice, sitting under the list it applies to. */}
        {isInvoiceVerification && !isProductTableLoading && (
          <div className="flex flex-wrap justify-end items-center gap-3 mx-2 mt-5 mb-4 pt-4 border-gray-200 border-t">
            {!hasPendingOcrChanges && (
              <p className="mr-auto text-gray-500 text-xs">Every invoice difference on this PO has been decided.</p>
            )}
            <button
              type="button"
              disabled={!hasPendingOcrChanges}
              className="inline-flex items-center bg-white shadow-sm px-5 py-2 border border-red-300 rounded-lg font-semibold text-red-600 text-xs whitespace-nowrap transition-colors hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={(e) => {
                e.preventDefault();
                setOcrBulkDecision("reject");
              }}
            >
              Reject all changes
            </button>
            <button
              type="button"
              disabled={!hasPendingOcrChanges}
              className="inline-flex items-center bg-black shadow-sm px-5 py-2 rounded-lg font-semibold text-white text-xs whitespace-nowrap transition-colors hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={(e) => {
                e.preventDefault();
                setOcrBulkDecision("accept");
              }}
            >
              Accept all changes
            </button>
          </div>
        )}
        <OcrBulkDecisionModal
          variant={ocrBulkDecision}
          changes={ocrBulkDecision ? ocrBulkChangeSummary(ocrBulkDecision) : []}
          isSubmitting={isProductTableLoading}
          onConfirm={handleConfirmBulkDecision}
          onGoBack={() => setOcrBulkDecision(null)}
        />
        {
          !isProductTableLoading && POProductsHook?.fields?.length > 0 && (
            <div className="flex flex-col justify-end float-right m-2 pr-32 gap-1">
              <p className="text-sm font-medium">
                Total Taxable Amount :{" "}
                <span className="font-semibold text text-xs 2xl:text-sm">
                  {" "}
                  {calculateSummary()?.taxable_amount?.toFixed(2)}
                </span>
              </p>
              <p className="font-medium text-xs 2xl:text-sm">
                Total Discounted Amount :{" "}
                <span className="font-semibold text"> {calculateSummary()?.discounted_amount?.toFixed(2)}</span>
              </p>
              <p className=" font-medium text-xs 2xl:text-sm">
                Net Cost :<span className="font-semibold text"> {calculateSummary()?.net_cost?.toFixed(2)}</span>
              </p>
              <p className="text-xs 2xl:text-sm font-medium">
                Tax Amount 5% :<span className="font-semibold text"> {calculateSummary()?.tax_5}</span>
              </p>
              <p className="text-xs 2xl:text-sm font-medium">
                Tax Amount 12% :<span className="font-semibold text"> {calculateSummary()?.tax_12}</span>
              </p>
              <p className="text-xs 2xl:text-sm font-medium">
                Tax Amount 18% :<span className="font-semibold text"> {calculateSummary()?.tax_18}</span>
              </p>
              <p className="text-xs 2xl:text-sm font-medium">
                Total Tax Amount:<span className="font-semibold text"> {calculateSummary()?.total_tax}</span>
              </p>
              <p className="text-xs 2xl:text-sm font-medium">
                Total cost after tax:
                <span className="font-semibold text"> {calculateSummary()?.total_cost_after_tax}</span>
              </p>
            </div>
          )
        }
        <BulkUploadModal
          onUploadSuccess={async (res) => {
            if (res.status) {
              setIsProductTableLoading(true);
              try {
                await fetchPODetails(formHook.getValues("po_id"));
              } finally {
                setIsProductTableLoading(false);
              }
            }
          }}
          POProductsHook={POProductsHook}
          formHook={formHook}
        />
      </div >
    )
  );
};

export default ProductInfoFormFields;
